import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { MenuProps } from 'antd';
import { RightOutlined } from '@ant-design/icons';
import type { AntdMenuItem } from '@shared/types/menu';

type MenuClickEvent = Parameters<NonNullable<MenuProps['onClick']>>[0];

interface SidebarSectionFlyoutProps {
  /** Обёртка вокруг рендера <Menu> — по её DOM ищем наведённый пункт с подменю */
  containerRef: React.RefObject<HTMLDivElement>;
  /** Top-level пункты меню — порядок и индекс 1:1 совпадают с DOM <li> внутри контейнера */
  items: Array<AntdMenuItem | null | undefined>;
  openKeys: string[];
  onClick?: MenuProps['onClick'];
  isDark: boolean;
  /** Выключить целиком — свёрнутый rail (там свой попап antd) или режим редактирования */
  enabled: boolean;
}

const CLOSE_DELAY_MS = 120;

// Собственная узкая форма вместо antd-объединения: divider/group-варианты
// структурно не несут label/children, и попытка их узнать через `Extract`
// по discriminated union ломается на пересечении с MenuDividerType.
interface MenuItemWithChildren {
  key: React.Key;
  label?: React.ReactNode;
  icon?: React.ReactNode;
  children: Array<AntdMenuItem | null | undefined>;
}

// Параметр — unknown, а не AntdMenuItem: type predicate обязан быть
// подтипом параметра, а MenuItemWithChildren (свободная children-форма) не
// подтип строгого antd ItemType[] (там undefined в детях запрещён, только
// null). unknown снимает это требование, проверка всё равно структурная.
function hasChildren(item: unknown): item is MenuItemWithChildren {
  if (!item || typeof item !== 'object') {
    return false;
  }
  const children = (item as { children?: unknown }).children;
  return Array.isArray(children) && children.length > 0;
}

/**
 * Сопоставляет DOM <li class="ant-menu-submenu"> с реальным пунктом меню — на
 * ЛЮБОЙ глубине вложенности, не только top-level. antd не даёт ключ пункта
 * напрямую на <li> для SubMenu-узлов (data-menu-id стоит только на leaf
 * MenuItem), поэтому ключ восстанавливается позиционно: индекс <li> среди
 * прямых детей его <ul> + рекурсивное разрешение родительского <ul> тем же
 * способом до корня. Порядок в DOM гарантированно совпадает с порядком
 * `items`/`children` — antd не переставляет пункты.
 */
function resolveMenuItem(
  li: HTMLElement,
  rootUl: Element,
  topItems: Array<AntdMenuItem | null | undefined>,
): AntdMenuItem | null | undefined {
  const parentUl = li.parentElement;
  if (!parentUl) {
    return undefined;
  }
  const index = Array.prototype.indexOf.call(parentUl.children, li);
  if (index < 0) {
    return undefined;
  }
  if (parentUl === rootUl) {
    return topItems[index];
  }
  const parentLi = parentUl.closest('li.ant-menu-submenu') as HTMLElement | null;
  if (!parentLi) {
    return undefined;
  }
  const parentItem = resolveMenuItem(parentLi, rootUl, topItems);
  if (!hasChildren(parentItem)) {
    return undefined;
  }
  return parentItem.children[index];
}

interface FlyoutPanelProps {
  item: MenuItemWithChildren;
  anchorRect: DOMRect;
  isDark: boolean;
  onNavigate: (key: string) => void;
  level: number;
  /** Родительская панель хочет знать, что курсор ушёл в дочернюю — не закрываться сама */
  onEnter?: () => void;
  onLeave?: () => void;
}

/**
 * Одна всплывающая панель подменю. Рекурсивна: если внутри панели наводят на
 * строку, у которой у самой есть дети, — раскрывает ВТОРУЮ панель сбоку от
 * неё (та же логика на любую глубину — «дальнейшая вложенность»).
 */
function FlyoutPanel({ item, anchorRect, isDark, onNavigate, level, onEnter, onLeave }: FlyoutPanelProps) {
  const [hoveredChildKey, setHoveredChildKey] = useState<string | null>(null);
  const [childRect, setChildRect] = useState<DOMRect | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  const cancelClose = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const scheduleClose = () => {
    cancelClose();
    closeTimerRef.current = setTimeout(() => setHoveredChildKey(null), CLOSE_DELAY_MS);
  };

  useEffect(() => () => cancelClose(), []);

  useLayoutEffect(() => {
    if (!panelRef.current) {
      return;
    }
    const el = panelRef.current;
    const height = el.offsetHeight;
    const maxTop = window.innerHeight - height - 8;
    const top = Math.max(8, Math.min(anchorRect.top, maxTop));
    el.style.top = `${top}px`;
  }, [anchorRect]);

  const hoveredChild = hoveredChildKey
    ? item.children.find((child) => String(child?.key ?? '') === hoveredChildKey)
    : null;
  const hoveredChildHasChildren = hasChildren(hoveredChild);

  return createPortal(
    <div
      ref={panelRef}
      onMouseEnter={() => {
        cancelClose();
        onEnter?.();
      }}
      onMouseLeave={() => {
        scheduleClose();
        onLeave?.();
      }}
      style={{
        position: 'fixed',
        left: anchorRect.right + 4,
        top: anchorRect.top,
        minWidth: 200,
        maxWidth: 320,
        maxHeight: 'calc(100vh - 16px)',
        overflowY: 'auto',
        borderRadius: 8,
        boxShadow: isDark
          ? '0 6px 16px rgba(0,0,0,0.5), 0 3px 6px rgba(0,0,0,0.4)'
          : '0 6px 16px rgba(0,0,0,0.12), 0 3px 6px rgba(0,0,0,0.08)',
        background: isDark ? '#1f1f1f' : '#fff',
        color: isDark ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.88)',
        padding: '4px',
        zIndex: 1050 + level,
      }}
    >
      <div
        style={{
          padding: '8px 12px 4px',
          fontSize: 12,
          fontWeight: 600,
          opacity: 0.55,
          whiteSpace: 'nowrap',
        }}
      >
        {item.label}
      </div>
      {item.children.map((child) => {
        if (!child || child.type === 'divider') {
          return null;
        }
        const childKey = String(child.key ?? '');
        const childHasChildren = hasChildren(child);
        const isHovered = hoveredChildKey === childKey;
        return (
          <div
            key={childKey}
            role="menuitem"
            onClick={() => {
              if (!childHasChildren) {
                onNavigate(childKey);
              }
            }}
            onMouseEnter={(e) => {
              cancelClose();
              setHoveredChildKey(childKey);
              setChildRect(e.currentTarget.getBoundingClientRect());
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '6px 12px',
              borderRadius: 4,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              transition: 'background-color 0.15s ease',
              backgroundColor: isHovered
                ? isDark
                  ? 'rgba(255,255,255,0.08)'
                  : 'rgba(24,144,255,0.08)'
                : 'transparent',
            }}
          >
            {'icon' in child && child.icon ? (child.icon as React.ReactNode) : null}
            <span style={{ flex: 1 }}>{child.label as React.ReactNode}</span>
            {childHasChildren && <RightOutlined style={{ fontSize: 10, opacity: 0.5 }} />}
          </div>
        );
      })}
      {hoveredChild && childRect && hoveredChildHasChildren && (
        <FlyoutPanel
          item={hoveredChild}
          anchorRect={childRect}
          isDark={isDark}
          onNavigate={onNavigate}
          level={level + 1}
          onEnter={cancelClose}
          onLeave={scheduleClose}
        />
      )}
    </div>,
    document.body,
  );
}

/**
 * GitLab-паттерн: в РАСКРЫТОМ сайдбаре у пункта, который ещё НЕ раскрыт кликом
 * (нет в openKeys), наведение мышью показывает всплывающее подменю сбоку —
 * так же, как в свёрнутом rail-режиме. Пункт, уже раскрытый кликом (inline,
 * push-down), флаут не показывает — его дети и так видны под ним. Работает
 * на любой глубине вложенности — см. resolveMenuItem и рекурсию FlyoutPanel.
 *
 * antd Menu не даёт эту комбинацию (push-down по клику + popup по hover) из
 * одного mode, поэтому флаут — свой, поверх штатного mode="inline" Menu.
 */
export function SidebarSectionFlyout({
  containerRef,
  items,
  openKeys,
  onClick,
  isDark,
  enabled,
}: SidebarSectionFlyoutProps) {
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  const [hoveredItem, setHoveredItem] = useState<MenuItemWithChildren | null>(null);
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const suppressedKeyRef = useRef<string | null>(null);
  const prevOpenKeysRef = useRef<string[]>(openKeys);

  const cancelClose = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const scheduleClose = () => {
    cancelClose();
    closeTimerRef.current = setTimeout(() => {
      setHoveredKey(null);
      setHoveredItem(null);
    }, CLOSE_DELAY_MS);
  };

  // Если пункт только что схлопнули кликом, пока мышь ещё над ним — не давать
  // флауту тут же выскочить обратно (иначе клик "свернуть" визуально ничего не
  // меняет). Снимается по реальному mouseleave этой строки, см. handleMouseOut.
  useEffect(() => {
    const prevOpenKeys = prevOpenKeysRef.current;
    prevOpenKeysRef.current = openKeys;
    const justClosed = prevOpenKeys.filter((k) => !openKeys.includes(k));
    if (justClosed.length > 0) {
      const [closedKey] = justClosed;
      suppressedKeyRef.current = closedKey;
      if (hoveredKey === closedKey) {
        setHoveredKey(null);
        setHoveredItem(null);
      }
    }
  }, [openKeys, hoveredKey]);

  useEffect(() => {
    if (!enabled) {
      setHoveredKey(null);
      setHoveredItem(null);
      return undefined;
    }

    const container = containerRef.current;
    if (!container) {
      return undefined;
    }

    const handleMouseOver = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      const li = target?.closest('li.ant-menu-submenu') as HTMLElement | null;
      if (!li || !container.contains(li)) {
        return;
      }

      const rootUl = container.querySelector(':scope > ul.ant-menu');
      if (!rootUl) {
        return;
      }

      const item = resolveMenuItem(li, rootUl, items);
      if (!hasChildren(item)) {
        cancelClose();
        setHoveredKey(null);
        setHoveredItem(null);
        return;
      }

      const key = String(item.key);
      if (openKeys.includes(key)) {
        // Уже раскрыт кликом (inline push-down) — дети видны на месте, флаут не нужен
        cancelClose();
        setHoveredKey(null);
        setHoveredItem(null);
        return;
      }

      if (suppressedKeyRef.current === key) {
        return;
      }

      cancelClose();
      setHoveredKey(key);
      setHoveredItem(item);
      setAnchorRect(li.getBoundingClientRect());
    };

    const handleMouseOut = (e: MouseEvent) => {
      const related = e.relatedTarget as Node | null;
      if (related && container.contains(related)) {
        return;
      }
      const target = e.target as HTMLElement | null;
      const li = target?.closest('li.ant-menu-submenu') as HTMLElement | null;
      if (li) {
        const rootUl = container.querySelector(':scope > ul.ant-menu');
        const item = rootUl ? resolveMenuItem(li, rootUl, items) : undefined;
        if (item && suppressedKeyRef.current === String(item.key)) {
          suppressedKeyRef.current = null;
        }
      }
      scheduleClose();
    };

    container.addEventListener('mouseover', handleMouseOver);
    container.addEventListener('mouseout', handleMouseOut);
    return () => {
      container.removeEventListener('mouseover', handleMouseOver);
      container.removeEventListener('mouseout', handleMouseOut);
      cancelClose();
    };
  }, [containerRef, items, openKeys, enabled]);

  if (!enabled || !hoveredItem || !anchorRect) {
    return null;
  }

  return (
    <FlyoutPanel
      item={hoveredItem}
      anchorRect={anchorRect}
      isDark={isDark}
      level={0}
      onEnter={cancelClose}
      onLeave={scheduleClose}
      onNavigate={(key) => {
        onClick?.({ key } as MenuClickEvent);
        setHoveredKey(null);
        setHoveredItem(null);
      }}
    />
  );
}
